/**
 * StopPlanModal - Multi-step cancellation flow
 * Step 1: Confirmation
 * Step 2: Survey (reason for cancelling)
 * Step 3: Processing / Success
 */

import { useState } from "react";
import {
  Box,
  Dialog,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { QuizButton } from "./shared/QuizButton";
import { PawsLoader } from "./shared/PawsLoader";
import { QUIZ } from "@/theme/quizTokens";

const CANCELLATION_REASONS = [
  { value: "too_expensive", label: "It's too expensive" },
  { value: "dog_doesnt_like", label: "My dog doesn't like the food" },
  { value: "too_much_food", label: "Too much food / waste" },
  { value: "switching_brands", label: "Switching to another brand" },
  { value: "dog_health_issues", label: "My dog has health issues" },
  { value: "dog_passed_away", label: "My dog passed away" },
  { value: "temporary_pause", label: "I just need a temporary break" },
  { value: "delivery_issues", label: "Delivery problems" },
  { value: "other", label: "Other reason" },
];

type Step = "confirm" | "survey" | "processing" | "success" | "error";

type Props = {
  open: boolean;
  onClose: (success?: boolean) => void;
  subscriptionId: string;
  dogName?: string;
  onConfirm: (reason: string, feedback: string) => Promise<void>;
};

export function StopPlanModal({
  open,
  onClose,
  subscriptionId,
  dogName,
  onConfirm,
}: Props) {
  const [step, setStep] = useState<Step>("confirm");
  const [selectedReason, setSelectedReason] = useState("");
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleClose = (success?: boolean) => {
    // Reset state on close
    setStep("confirm");
    setSelectedReason("");
    setFeedback("");
    setError(null);
    onClose(success);
  };

  const handleProceedToSurvey = () => {
    setStep("survey");
  };

  const handleSubmit = async () => {
    if (!selectedReason) return;

    setStep("processing");
    setError(null);

    try {
      await onConfirm(selectedReason, feedback);
      setStep("success");
      // Auto-close after 2 seconds on success
      setTimeout(() => {
        handleClose(true);
      }, 2000);
    } catch (err: any) {
      setError(err?.message || "Failed to cancel subscription");
      setStep("error");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={step === "processing" ? undefined : () => handleClose(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 3 },
          background: QUIZ.color.cream,
          maxHeight: { xs: "100%", sm: "90vh" },
          height: { xs: "100%", sm: "auto" },
          m: { xs: 0, sm: 2 },
          width: { xs: "100%", sm: "auto" },
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Scrollable Content */}
      <Box sx={{ flex: 1, overflow: "auto", p: { xs: 2.5, md: 4 } }}>
        {/* Step 1: Confirmation */}
        {step === "confirm" && (
          <Stack spacing={3} alignItems="center" textAlign="center">
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                background: "#FFF3CD",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <WarningAmberIcon sx={{ fontSize: 40, color: "#CE7A00" }} />
            </Box>

            <Typography
              sx={{
                fontSize: { xs: 20, md: 24 },
                fontWeight: 800,
                color: QUIZ.color.brown,
              }}
            >
              Are you sure you want to cancel?
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: 14, md: 16 },
                color: QUIZ.color.brown,
                opacity: 0.8,
              }}
            >
              {dogName
                ? `We'll miss seeing ${dogName}! If you cancel, your subscription will be stopped and no future orders will be placed.`
                : "If you cancel, your subscription will be stopped and no future orders will be placed."}
            </Typography>

            <Box
              sx={{
                background: QUIZ.color.cream2,
                borderRadius: 2,
                p: 2,
                width: "100%",
              }}
            >
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: QUIZ.color.brown,
                }}
              >
                Before you go...
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  color: QUIZ.color.brown,
                  mt: 0.5,
                }}
              >
                Need a break? You can skip deliveries or change your delivery
                frequency instead of cancelling completely.
              </Typography>
            </Box>
          </Stack>
        )}

        {/* Step 2: Survey */}
        {step === "survey" && (
          <Stack spacing={3}>
            <Typography
              sx={{
                fontSize: { xs: 20, md: 24 },
                fontWeight: 800,
                color: QUIZ.color.brown,
                textAlign: "center",
              }}
            >
              Help us improve
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: 14, md: 16 },
                color: QUIZ.color.brown,
                opacity: 0.8,
                textAlign: "center",
              }}
            >
              We'd love to know why you're leaving so we can do better.
            </Typography>

            <RadioGroup
              value={selectedReason}
              onChange={(e) => setSelectedReason(e.target.value)}
            >
              <Stack spacing={1}>
                {CANCELLATION_REASONS.map((reason) => (
                  <Box
                    key={reason.value}
                    sx={{
                      border:
                        selectedReason === reason.value
                          ? "2px solid #4A3026"
                          : "1px solid rgba(0,0,0,0.1)",
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      background:
                        selectedReason === reason.value
                          ? "rgba(74, 48, 38,0.08)"
                          : QUIZ.color.white,
                      cursor: "pointer",
                      transition: "all 0.2s",
                      "&:hover": {
                        borderColor: QUIZ.color.brown,
                      },
                    }}
                    onClick={() => setSelectedReason(reason.value)}
                  >
                    <FormControlLabel
                      value={reason.value}
                      control={
                        <Radio
                          sx={{
                            color: QUIZ.color.brown,
                            "&.Mui-checked": { color: QUIZ.color.brown },
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: QUIZ.color.brown,
                          }}
                        >
                          {reason.label}
                        </Typography>
                      }
                      sx={{ m: 0, width: "100%" }}
                    />
                  </Box>
                ))}
              </Stack>
            </RadioGroup>

            {selectedReason === "other" && (
              <TextField
                multiline
                rows={3}
                placeholder="Please tell us more..."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    background: QUIZ.color.warmWhite,
                  },
                }}
              />
            )}
          </Stack>
        )}

        {/* Step 3: Processing */}
        {step === "processing" && (
          <PawsLoader
            message="Processing your request..."
            fullScreen={false}
            size="medium"
          />
        )}

        {/* Success */}
        {step === "success" && (
          <Stack spacing={3} alignItems="center" textAlign="center">
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                background: "#E8F5E9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 40, color: "#2E7D32" }} />
            </Box>

            <Typography
              sx={{
                fontSize: { xs: 20, md: 24 },
                fontWeight: 800,
                color: QUIZ.color.brown,
              }}
            >
              Subscription cancelled
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: 14, md: 16 },
                color: QUIZ.color.brown,
                opacity: 0.8,
              }}
            >
              Your subscription has been cancelled. We're sorry to see you go!
              You can restart your subscription anytime from your account.
            </Typography>
          </Stack>
        )}

        {/* Error */}
        {step === "error" && (
          <Stack spacing={3} alignItems="center" textAlign="center">
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                background: "#FFEBEE",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <WarningAmberIcon sx={{ fontSize: 40, color: "#D14C3F" }} />
            </Box>

            <Typography
              sx={{
                fontSize: { xs: 20, md: 24 },
                fontWeight: 800,
                color: QUIZ.color.brown,
              }}
            >
              Something went wrong
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: 14, md: 16 },
                color: "#D14C3F",
              }}
            >
              {error || "Failed to cancel subscription. Please try again."}
            </Typography>
          </Stack>
        )}
      </Box>

      {/* Sticky Footer */}
      {step === "confirm" && (
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            borderTop: "1px solid rgba(0,0,0,.08)",
            background: QUIZ.color.cream,
            flexShrink: 0,
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <QuizButton
              variant="secondary"
              onClick={() => handleClose(false)}
              fullWidth
            >
              Keep My Plan
            </QuizButton>
            <QuizButton
              variant="primary"
              onClick={handleProceedToSurvey}
              fullWidth
              sx={{
                background: "#D14C3F",
                "&:hover": { background: "#B8403A" },
              }}
            >
              Continue Cancellation
            </QuizButton>
          </Stack>
        </Box>
      )}

      {step === "survey" && (
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            borderTop: "1px solid rgba(0,0,0,.08)",
            background: QUIZ.color.cream,
            flexShrink: 0,
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <QuizButton
              variant="secondary"
              onClick={() => setStep("confirm")}
              fullWidth
            >
              Go Back
            </QuizButton>
            <QuizButton
              variant="primary"
              onClick={handleSubmit}
              disabled={!selectedReason}
              fullWidth
              sx={{
                background: "#D14C3F",
                "&:hover": { background: "#B8403A" },
                "&:disabled": {
                  background: "#ccc",
                  color: "#666",
                },
              }}
            >
              Cancel Subscription
            </QuizButton>
          </Stack>
        </Box>
      )}

      {step === "success" && (
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            borderTop: "1px solid rgba(0,0,0,.08)",
            background: QUIZ.color.cream,
            flexShrink: 0,
          }}
        >
          <QuizButton variant="primary" onClick={() => handleClose(true)} fullWidth>
            Close
          </QuizButton>
        </Box>
      )}

      {step === "error" && (
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            borderTop: "1px solid rgba(0,0,0,.08)",
            background: QUIZ.color.cream,
            flexShrink: 0,
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <QuizButton variant="secondary" onClick={() => handleClose(false)} fullWidth>
              Close
            </QuizButton>
            <QuizButton
              variant="primary"
              onClick={handleSubmit}
              fullWidth
              sx={{
                background: "#D14C3F",
                "&:hover": { background: "#B8403A" },
              }}
            >
              Try Again
            </QuizButton>
          </Stack>
        </Box>
      )}
    </Dialog>
  );
}
